import CustomWebView from '@/components/CustomWebView/ResultsCharts';
import ConfirmModal from '@/components/SharedComponents/ConfirmModal/ConfirmModal';
import {
  useResponsiveFontSize,
  useResponsiveHeight,
} from '@/components/Util/reactResponsiveDimensions';
import colors from '@/config/colors';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { logError } from '@/utils/logHandling';
import { StackScreenProps } from '@react-navigation/stack';
import { useEffect, useRef, useState } from 'react';
import { Linking, Platform, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Text';
import { isPatientApp } from '@/config/constants';
import { Modal } from '@/components/ui/therapist/Modal';
import { PrimaryButton } from '@/components/Button';
import { Links } from '@/config/links';

interface ResultDetailProps extends StackScreenProps<
  TherapistStackParamList,
  'ResultDetailScreen'
> {
  handleDownloadLinksClickState: boolean;
  changeHandleDownloadLinksClickState: (arg: boolean) => void;
  timeSeriesData: unknown;
  individualStatsData: unknown;
  webViewTitle: string;
}

const html2canvas = Platform.select({
  // On Native just adding this library since in both cases the html2canvas will be implemented inside the web only
  /* eslint-disable @typescript-eslint/no-require-imports */
  native: () => require('@/components/Util/reactResponsiveDimensions'),
  default: () => require('html2canvas'),
})();

const styles = StyleSheet.create({
  innerStyle: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  container: { flex: 1 },
  textStyle: {
    color: colors.darkPurpleBlue,
  },
  padding7: {
    padding: 7,
  },
});
function ResultDetail({
  route,
  handleDownloadLinksClickState,
  changeHandleDownloadLinksClickState,
  timeSeriesData,
  individualStatsData,
  webViewTitle,
}: ResultDetailProps) {
  const [showResultsDownloadModal, setShowResultsDownloadModal] =
    useState(false);
  const [mobileDownloadRedirectModal, setMobileDownloadRedirectModal] =
    useState(false);
  const dataType = useRef('');
  const exportRef = useRef<HTMLIFrameElement | null>(null);

  // Variables
  const { exerciseSetName, exerciseClass } = route.params;
  const responsiveHeight = useResponsiveHeight(100);
  const isFullscreen = Platform.OS !== 'web' && responsiveHeight < 500;

  const DICTIONARY = { results: 'Ergebnisstatistik' };
  const responsiveStyles = StyleSheet.create({
    container: {
      flexDirection: 'column',
      alignItems: 'center',
    },
  });

  const handleDownloadLinksClick = (type: string) => {
    if (Platform.OS === 'web') {
      dataType.current = '';
      if (type === 'results') setShowResultsDownloadModal(true);
    } else {
      dataType.current = DICTIONARY[type as keyof typeof DICTIONARY] ?? '';
      setMobileDownloadRedirectModal(true);
    }
  };

  const handleBrowserRedirectClick = () => {
    // openURL("https://aphasie.neolexon.de");
    setMobileDownloadRedirectModal(false);
  };

  const handleWebClickDownload = (type: string) => {
    return () => {
      // type param is saved as a closure for using the same function
      if (type === 'results' && exportRef.current) {
        exportAsImage(exportRef.current, 'results');
        setShowResultsDownloadModal(false);
      }
    };
  };

  useEffect(() => {
    if (handleDownloadLinksClickState) {
      changeHandleDownloadLinksClickState(false);
      handleDownloadLinksClick('results');
    }
  }, [handleDownloadLinksClickState]);

  const exportAsImage = async (
    el: HTMLIFrameElement,
    imageFileName: string,
  ) => {
    const windowX = el.contentWindow;
    html2canvas(windowX?.document?.body, {
      allowTaint: true,
      useCORS: true,
    }).then(function (canvas: HTMLCanvasElement) {
      canvas.style.display = 'none';
      try {
        if (window === undefined) return;
        window?.document?.body?.appendChild(canvas);
        const image = canvas
          .toDataURL('image/png')
          .replace('image/png', 'image/octet-stream');
        const a = window?.document?.createElement('a');
        a.setAttribute('download', imageFileName + '.png');
        a.setAttribute('href', image);
        a.click();
      } catch (error) {
        logError('Image export failed', error);
      }
    });
  };

  return (
    <View style={styles.container}>
      <View>
        <View style={responsiveStyles.container}>
          {isPatientApp ? (
            <ConfirmModal
              isVisible={showResultsDownloadModal}
              cancelText={'Abbrechen'}
              confirmText={'Herunterladen'}
              confirmAction={handleWebClickDownload('results')}
              cancelAction={() => setShowResultsDownloadModal(false)}
              headerText={'Ergebnisstatistik herunterladen'}
              contentText={
                'Wollen Sie Ihre Ergebnisstatistik wirklich herunterladen? Dies sollte nur auf eigenen Geräten durchgeführt werden, um zu verhindern, dass Unbefugte Zugriff auf die heruntergeladenen Daten erhalten.'
              }
            />
          ) : (
            <Modal
              title="Ergebnisstatistik herunterladen"
              onClose={() => setShowResultsDownloadModal(false)}
              isVisible={showResultsDownloadModal}
              confirmActionButton={
                <PrimaryButton
                  onPress={handleWebClickDownload('results')}
                  label="Herunterladen"
                />
              }
              cancelActionButton={
                <PrimaryButton
                  look="neutral"
                  onPress={() => setShowResultsDownloadModal(false)}
                  label="Abbrechen"
                />
              }
            >
              <Text style={{ fontSize: 16 }}>
                Wollen Sie Ihre Ergebnisstatistik wirklich herunterladen? Dies
                sollte nur auf eigenen Geräten durchgeführt werden, um zu
                verhindern, dass Unbefugte Zugriff auf die heruntergeladenen
                Daten erhalten.
              </Text>
            </Modal>
          )}

          {isPatientApp ? (
            <ConfirmModal
              isFullscreen={isFullscreen}
              isVisible={mobileDownloadRedirectModal}
              confirmText={'Schließen'}
              confirmAction={handleBrowserRedirectClick}
              headerText={'Nur in der Browser-App möglich'}
              isContentComponent
              contentText={
                <View style={[styles.innerStyle, styles.padding7]}>
                  <Text
                    style={{
                      fontSize: useResponsiveFontSize(2.2),
                      lineHeight: useResponsiveFontSize(4),
                    }}
                  >
                    Das Herunterladen der {dataType.current} ist in der
                    Browser-App am Computer auf{' '}
                    <Text
                      style={[
                        styles.textStyle,
                        { fontSize: useResponsiveFontSize(2.2) },
                      ]}
                      onPress={() =>
                        Linking.openURL(Links.aphasieBrowserApp.href)
                      }
                    >
                      {Links.aphasieBrowserApp.label}
                    </Text>{' '}
                    unter “Ergebnisse ansehen” möglich.
                  </Text>
                </View>
              }
            />
          ) : (
            <Modal
              title="Nur in der Browser-App möglich"
              cancelActionButton={
                <PrimaryButton
                  onPress={() => setMobileDownloadRedirectModal(false)}
                  label="Schließen"
                />
              }
              onClose={() => setMobileDownloadRedirectModal(false)}
              isVisible={mobileDownloadRedirectModal}
            >
              <View>
                <Text
                  style={{
                    lineHeight: useResponsiveFontSize(1.8),
                  }}
                >
                  Das Herunterladen der {dataType.current} ist in der
                  Browser-App am Computer auf{' '}
                  <Text
                    style={styles.textStyle}
                    onPress={() =>
                      Linking.openURL(Links.therapistBrowserApp.href)
                    }
                  >
                    {Links.therapistBrowserApp.label}
                  </Text>{' '}
                  unter “Ergebnisse ansehen” möglich.
                </Text>
              </View>
            </Modal>
          )}
        </View>
      </View>
      <View style={styles.innerStyle}>
        <CustomWebView
          ref={exportRef}
          individualStatsData={individualStatsData}
          timeSeriesData={timeSeriesData}
          appDisplayName={exerciseSetName}
          setType={exerciseClass === 'wordset' ? 'Wortset' : 'Satzset'}
          webViewTitle={webViewTitle}
        />
      </View>
    </View>
  );
}

export default ResultDetail;
